import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  FiAlertTriangle,
  FiChevronDown,
  FiChevronRight,
  FiCopy,
  FiDownload,
  FiEdit2,
  FiRefreshCw,
  FiTrendingUp,
} from 'react-icons/fi';
import { useI18n } from '../../i18n';
import {
  analyseProfitability,
  currentMinimumHourlyRate,
  profitabilityService,
  requiredUpliftPercent,
  type ContractMargin,
  type ContractScenario,
  type MarginLine,
  type PayScenario,
  type ProfitabilityWorkspace,
  type ScenarioLine,
} from '../../services/profitabilityService';
import ChargeRateModal from './ChargeRateModal';
import {
  buildProfitabilityCsv,
  contractLabel,
  formatDate,
  formatHours,
  formatMoney,
  formatMoneyRounded,
  formatPercent,
  formatSignedPercent,
  localDateKey,
  outcomeLabel,
  outcomeTone,
  PERIOD_KEYS,
  periodLabel,
  periodRange,
  previousRatesLabel,
  scenarioHeadline,
  statusLabel,
  statusTone,
  type PeriodKey,
} from './profitabilityPresentation';
import './ProfitabilityPage.css';

const parseAmount = (value: string): number | null => {
  const parsed = Number(value.replace(',', '.'));
  return value.trim() !== '' && Number.isFinite(parsed) ? parsed : null;
};

/** An example to start from: five per cent over today's minimum, to the nearest five cent. */
const suggestedMinimum = (currentMinimum: number): string => (
  (Math.round(currentMinimum * 1.05 * 20) / 20).toFixed(2)
);

const contractKey = (contractId: string | null): string => contractId ?? 'no-client';

/** A rise smaller than this rounds to nothing and is not worth asking a client for. */
const MIN_UPLIFT_PERCENT = 0.05;

interface RateModalTarget {
  contract: { id: string; name: string };
  siteId: string | null;
}

interface LineCellsProps {
  line: MarginLine;
  previous: MarginLine | null;
  showPrevious: boolean;
  scenario: ScenarioLine | null;
  showScenario: boolean;
  targetMarginPercent: number;
  currencyCode: string;
  onSetRate?: () => void;
}

/** The figures of one row, shared by a contract and the sites under it. */
function LineCells({
  line,
  previous,
  showPrevious,
  scenario,
  showScenario,
  targetMarginPercent,
  currencyCode,
  onSetRate,
}: LineCellsProps) {
  const { t } = useI18n();
  const isPriced = line.status !== 'no_charge_rate';
  // Holding a margin only means something where there is one: a contract that
  // already loses money is shown what reaching the target would take instead.
  const holdsMargin = scenario !== null && scenario.before.status !== 'loss';
  const uplift = holdsMargin
    ? scenario.upliftToHoldMargin
    : scenario
      ? scenario.upliftToReachTarget
      : requiredUpliftPercent(line.revenue, line.labourCost, targetMarginPercent);
  const neededRate = uplift !== null && uplift > MIN_UPLIFT_PERCENT && line.averageChargeRate !== null
    ? line.averageChargeRate * (1 + (uplift / 100))
    : null;

  return (
    <>
      <td className="pf-table__num">{formatHours(line.billableHours)}</td>
      <td className="pf-table__num">
        {isPriced ? formatMoney(line.averageChargeRate, currencyCode) : onSetRate ? (
          <button type="button" className="pf-link" onClick={onSetRate}>{t('Set charge rate')}</button>
        ) : '--'}
      </td>
      <td className="pf-table__num">{formatMoney(line.costPerBillableHour, currencyCode)}</td>
      <td className="pf-table__num">
        {isPriced ? (
          <span className={`pf-margin pf-tone--${statusTone(line.status)}`}>
            <strong>{formatPercent(line.marginPercent)}</strong>
            <span>{formatMoney(line.margin, currencyCode)}</span>
          </span>
        ) : '--'}
      </td>
      {showPrevious && (
        <td className="pf-table__num pf-table__muted">
          {previous && previous.status !== 'no_charge_rate' ? formatPercent(previous.marginPercent) : '--'}
        </td>
      )}
      {showScenario && (
        <td className="pf-table__num">
          {scenario && scenario.outcome !== 'unpriced' ? (
            <span className={`pf-margin pf-tone--${outcomeTone(scenario.outcome)}`}>
              <strong>{formatPercent(scenario.after.marginPercent)}</strong>
              <span>{outcomeLabel(scenario.outcome, t)}</span>
            </span>
          ) : '--'}
        </td>
      )}
      <td className="pf-table__num">
        {neededRate !== null ? (
          <span className="pf-margin">
            <strong>{formatSignedPercent(uplift)}</strong>
            <span>
              {holdsMargin
                ? t('{rate} holds the margin', { rate: formatMoney(neededRate, currencyCode) })
                : t('{rate} reaches the target', { rate: formatMoney(neededRate, currencyCode) })}
            </span>
          </span>
        ) : isPriced ? t('None') : '--'}
      </td>
    </>
  );
}

export default function ProfitabilityPage() {
  const { t, tn } = useI18n();
  const today = useMemo(() => localDateKey(), []);

  const [periodKey, setPeriodKey] = useState<PeriodKey>('last_4_weeks');
  const [workspace, setWorkspace] = useState<ProfitabilityWorkspace | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isBusy, setIsBusy] = useState(false);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');

  const [minimumRate, setMinimumRate] = useState('');
  const [unsocialPremium, setUnsocialPremium] = useState('');
  const [keepDifferentials, setKeepDifferentials] = useState(false);

  const [onCostPercent, setOnCostPercent] = useState('');
  const [targetPercent, setTargetPercent] = useState('');

  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());
  const [rateModal, setRateModal] = useState<RateModalTarget | null>(null);

  const load = useCallback(async (key: PeriodKey) => {
    setIsLoading(true);
    setError('');
    try {
      const next = await profitabilityService.loadWorkspace(periodRange(key, today), today);
      setWorkspace(next);
      setOnCostPercent(String(next.input.settings.employerOnCostPercent));
      setTargetPercent(String(next.input.settings.targetMarginPercent));
      // Seeded once: a figure somebody has typed is theirs to keep across periods.
      setMinimumRate((current) => current || suggestedMinimum(currentMinimumHourlyRate(next.input)));
    } catch (err) {
      setError((err as Error).message || t('Could not load profitability.'));
    } finally {
      setIsLoading(false);
    }
  }, [t, today]);

  useEffect(() => { void load(periodKey); }, [load, periodKey]);

  useEffect(() => {
    if (!toast) return undefined;
    const timer = window.setTimeout(() => setToast(''), 3200);
    return () => window.clearTimeout(timer);
  }, [toast]);

  const scenario = useMemo<PayScenario | null>(() => {
    const rate = parseAmount(minimumRate);
    if (rate === null || rate <= 0) return null;
    const premium = parseAmount(unsocialPremium);
    return {
      minimumHourlyRate: rate,
      unsocialShiftPremium: premium !== null && premium >= 0 ? premium : null,
      keepDifferentials,
    };
  }, [minimumRate, unsocialPremium, keepDifferentials]);

  // Pure, so a change to the scenario re-prices the roster without a round trip.
  const analysis = useMemo(
    () => (workspace ? analyseProfitability(workspace.input, scenario) : null),
    [workspace, scenario],
  );

  const currentMinimum = useMemo(
    () => (workspace ? currentMinimumHourlyRate(workspace.input) : null),
    [workspace],
  );

  const previousByContract = useMemo(
    () => new Map(analysis?.previous?.report.contracts.map((contract) => [contract.contractId, contract]) ?? []),
    [analysis],
  );
  const scenarioByContract = useMemo(
    () => new Map(analysis?.scenario?.contracts.map((contract) => [contract.contractId, contract]) ?? []),
    [analysis],
  );

  const currencyCode = workspace?.currencyCode ?? 'EUR';
  const settings = workspace?.input.settings ?? null;
  const report = analysis?.current ?? null;
  const showPrevious = Boolean(analysis?.previous);
  const showScenario = Boolean(analysis?.scenario);

  const toggleExpanded = (key: string) => {
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleSaveSettings = async (event: React.FormEvent) => {
    event.preventDefault();
    const onCost = parseAmount(onCostPercent);
    const target = parseAmount(targetPercent);
    if (onCost === null || onCost < 0 || onCost > 200) {
      setError(t('Employer on-costs must be between 0% and 200% of pay.'));
      return;
    }
    if (target === null || target < 0 || target >= 100) {
      setError(t('Target margin must be at least 0% and under 100%.'));
      return;
    }

    setIsBusy(true);
    setError('');
    try {
      await profitabilityService.saveSettings({ employerOnCostPercent: onCost, targetMarginPercent: target });
      setWorkspace((current) => (current ? {
        ...current,
        input: {
          ...current.input,
          settings: { employerOnCostPercent: onCost, targetMarginPercent: target, source: 'saved' },
        },
      } : current));
      setToast(t('Assumptions saved'));
    } catch (err) {
      setError((err as Error).message || t('Something went wrong'));
    } finally {
      setIsBusy(false);
    }
  };

  /** Charge rates changed: read them again and re-price, without re-reading the roster. */
  const handleRatesChanged = async () => {
    const chargeRates = await profitabilityService.fetchChargeRates();
    setWorkspace((current) => (current ? {
      ...current,
      input: { ...current.input, context: { ...current.input.context, chargeRates } },
    } : current));
  };

  const handleExport = () => {
    if (!report || !workspace) return;
    const csv = buildProfitabilityCsv(report.contracts, analysis?.scenario ?? null, t);
    const url = URL.createObjectURL(new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `profitability-${workspace.input.period.startDate}-to-${workspace.input.period.endDate}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  /** The case for a contract in sentences, ready to paste into an email to the client. */
  const buildSummary = (contract: ContractMargin, after: ContractScenario | undefined): string => {
    const name = contractLabel(contract, t);
    const lines = [
      t('{client}: {hours} billable hours from {start} to {end}.', {
        client: name,
        hours: formatHours(contract.billableHours),
        start: formatDate(workspace!.input.period.startDate),
        end: formatDate(workspace!.input.period.endDate),
      }),
      t('Charged {charge} an hour; labour costs {cost} an hour. Margin {margin} ({percent}).', {
        charge: formatMoney(contract.averageChargeRate, currencyCode),
        cost: formatMoney(contract.costPerBillableHour, currencyCode),
        margin: formatMoney(contract.margin, currencyCode),
        percent: formatPercent(contract.marginPercent),
      }),
    ];

    const previous = previousByContract.get(contract.contractId);
    if (analysis?.previous && previous && previous.status !== 'no_charge_rate') {
      lines.push(t('Before the minimum rose to {rate} on {date}, the margin was {percent}.', {
        rate: formatMoney(analysis.previous.rate, currencyCode),
        date: formatDate(analysis.previous.changeDate),
        percent: formatPercent(previous.marginPercent),
      }));
    }

    const priced = scenario && after && after.outcome !== 'unpriced' ? after : null;
    const rateAfter = (uplift: number) => formatMoney(
      (contract.averageChargeRate ?? 0) * (1 + (uplift / 100)),
      currencyCode,
    );

    if (scenario && priced) {
      lines.push(t('At a minimum of {rate} an hour, labour would cost {cost} an hour and the margin would be {percent}.', {
        rate: formatMoney(scenario.minimumHourlyRate, currencyCode),
        cost: formatMoney(priced.after.costPerBillableHour, currencyCode),
        percent: formatPercent(priced.after.marginPercent),
      }));
      if (
        priced.before.status !== 'loss'
        && priced.upliftToHoldMargin !== null
        && priced.upliftToHoldMargin > MIN_UPLIFT_PERCENT
      ) {
        lines.push(t('A charge rate of {rate} ({rise}) would keep the margin where it is today.', {
          rate: rateAfter(priced.upliftToHoldMargin),
          rise: formatSignedPercent(priced.upliftToHoldMargin),
        }));
      }
    }

    const toTarget = priced
      ? priced.upliftToReachTarget
      : requiredUpliftPercent(contract.revenue, contract.labourCost, workspace!.input.settings.targetMarginPercent);
    if (toTarget !== null && toTarget > MIN_UPLIFT_PERCENT) {
      lines.push(t('Reaching a {target} margin would take a charge rate of {rate} ({rise}).', {
        target: formatPercent(workspace!.input.settings.targetMarginPercent),
        rate: rateAfter(toTarget),
        rise: formatSignedPercent(toTarget),
      }));
    }

    return lines.join('\n');
  };

  const handleCopySummary = async (contract: ContractMargin) => {
    try {
      await navigator.clipboard.writeText(buildSummary(contract, scenarioByContract.get(contract.contractId)));
      setToast(t('Summary copied'));
    } catch {
      setError(t('Could not copy to the clipboard.'));
    }
  };

  const breakdownRows = (line: MarginLine): { label: string; amount: number }[] => [
    { label: t('Base pay'), amount: line.breakdown.basePay },
    { label: t('Overtime premium'), amount: line.breakdown.overtimePremium },
    { label: t('Sunday premium'), amount: line.breakdown.sundayPremium },
    { label: t('Public holiday premium'), amount: line.breakdown.publicHolidayPremium },
    { label: t('Unsocial-hours premium'), amount: line.breakdown.unsocialPremium },
    { label: t('Minimum shift payment'), amount: line.breakdown.minimumShiftTopUp },
    { label: t('Employer on-costs'), amount: line.onCost },
  ].filter((row) => row.amount !== 0);

  // The client column, then what LineCells renders.
  const columnCount = 6 + (showPrevious ? 1 : 0) + (showScenario ? 1 : 0);
  const attentionCount = report ? report.lossMakingCount + report.belowTargetCount : 0;

  return (
    <div className="pf-page">
      <header className="pf-hero">
        <div className="pf-hero__copy">
          <div className="pf-hero__eyebrow">{t('Profitability')}</div>
          <h1 className="pf-hero__title">{t('Cost and margin per contract')}</h1>
          <p className="pf-hero__subtitle">
            {t('The roster for the period, priced with the pay rules payroll uses at the rates in force today, and set against what each client is charged.')}
          </p>
        </div>
        <div className="pf-hero__actions">
          <div className="pf-segment" role="group" aria-label={t('Period')}>
            {PERIOD_KEYS.map((key) => (
              <button
                key={key}
                type="button"
                className={`pf-segment__item${key === periodKey ? ' pf-segment__item--active' : ''}`}
                aria-pressed={key === periodKey}
                onClick={() => setPeriodKey(key)}
              >
                {periodLabel(key, t)}
              </button>
            ))}
          </div>
          <button type="button" className="btn btn--secondary" onClick={() => { void load(periodKey); }} disabled={isLoading}>
            <FiRefreshCw aria-hidden="true" /> {t('Refresh')}
          </button>
          <button type="button" className="btn btn--secondary" onClick={handleExport} disabled={!report || report.contracts.length === 0}>
            <FiDownload aria-hidden="true" /> {t('Export CSV')}
          </button>
        </div>
      </header>

      {error && <div className="msg--error" role="alert">{error}</div>}
      {toast && <div className="pf-toast" role="status">{toast}</div>}

      {isLoading && !workspace && <div className="loading"><div className="spinner" /></div>}

      {workspace && report && settings && (
        <div className={`pf-body${isLoading ? ' pf-body--stale' : ''}`}>
          <p className="pf-period">
            {t('{start} to {end}', {
              start: formatDate(workspace.input.period.startDate),
              end: formatDate(workspace.input.period.endDate),
            })}
            {' · '}
            {tn('{count} rostered shift', '{count} rostered shifts', report.totals.shiftCount)}
            {report.totals.draftShiftCount > 0 && (
              <> {' · '}{tn('{count} still a draft', '{count} still drafts', report.totals.draftShiftCount)}</>
            )}
          </p>

          {settings.source === 'default' && (
            <div className="pf-callout pf-callout--info">
              <FiAlertTriangle aria-hidden="true" />
              <span>
                {t('These margins use starting assumptions: employer on-costs of {onCost} of pay and a target margin of {target}. Set your own under Assumptions.', {
                  onCost: formatPercent(settings.employerOnCostPercent),
                  target: formatPercent(settings.targetMarginPercent),
                })}
              </span>
            </div>
          )}
          {report.unpricedContractCount > 0 && (
            <div className="pf-callout pf-callout--warning">
              <FiAlertTriangle aria-hidden="true" />
              <span>
                {tn(
                  '{count} client has no charge rate for these shifts, so no margin can be stated for it. {amount} of labour cost is outside the figures below.',
                  '{count} clients have no charge rate for these shifts, so no margin can be stated for them. {amount} of labour cost is outside the figures below.',
                  report.unpricedContractCount,
                  { amount: formatMoneyRounded(report.unpricedLabourCost, currencyCode) },
                )}
              </span>
            </div>
          )}
          {report.totals.fallbackRateShiftCount > 0 && (
            <div className="pf-callout pf-callout--warning">
              <FiAlertTriangle aria-hidden="true" />
              <span>
                {tn(
                  '{count} shift is priced at the company base rate because its guard has no pay band or rank rate.',
                  '{count} shifts are priced at the company base rate because their guards have no pay band or rank rate.',
                  report.totals.fallbackRateShiftCount,
                )}
              </span>
            </div>
          )}

          {report.totals.shiftCount === 0 ? (
            <div className="empty-state">
              <div className="empty-state__title">{t('Nothing is rostered in this period.')}</div>
              <div className="empty-state__sub">{t('Margins are worked out from the duty board. Choose another period, or roster some shifts.')}</div>
            </div>
          ) : (
            <>
              <section className="pf-metrics" aria-label={t('Totals for priced contracts')}>
                <div className="pf-metric">
                  <div className="pf-metric__label">{t('Revenue')}</div>
                  <div className="pf-metric__value">{formatMoneyRounded(report.priced.revenue, currencyCode)}</div>
                  <div className="pf-metric__detail">
                    {t('{hours} billable hours', { hours: formatHours(report.priced.billableHours) })}
                  </div>
                </div>
                <div className="pf-metric">
                  <div className="pf-metric__label">{t('Labour cost')}</div>
                  <div className="pf-metric__value">{formatMoneyRounded(report.priced.labourCost, currencyCode)}</div>
                  <div className="pf-metric__detail">
                    {t('{pay} pay + {onCost} on-costs', {
                      pay: formatMoneyRounded(report.priced.grossPay, currencyCode),
                      onCost: formatMoneyRounded(report.priced.onCost, currencyCode),
                    })}
                  </div>
                </div>
                <div className={`pf-metric pf-tone--${statusTone(report.priced.status)}`}>
                  <div className="pf-metric__label">{t('Margin')}</div>
                  <div className="pf-metric__value">{formatPercent(report.priced.marginPercent)}</div>
                  <div className="pf-metric__detail">{formatMoneyRounded(report.priced.margin, currencyCode)}</div>
                </div>
                <div className={`pf-metric pf-tone--${report.lossMakingCount > 0 ? 'danger' : attentionCount > 0 ? 'warning' : 'success'}`}>
                  <div className="pf-metric__label">{t('Needs attention')}</div>
                  <div className="pf-metric__value">{attentionCount}</div>
                  <div className="pf-metric__detail">
                    {t('{loss} loss-making · {below} below target', {
                      loss: report.lossMakingCount,
                      below: report.belowTargetCount,
                    })}
                  </div>
                </div>
              </section>

              <section className="pf-panel">
                <div className="pf-panel__head">
                  <span className="pf-panel__icon"><FiTrendingUp aria-hidden="true" /></span>
                  <div>
                    <h2>{t('Model a pay increase')}</h2>
                    <p>
                      {currentMinimum !== null
                        ? t('Today nobody is paid under {rate} an hour. Try the next minimum and see which contracts it turns.', {
                          rate: formatMoney(currentMinimum, currencyCode),
                        })
                        : t('Try the next minimum hourly rate and see which contracts it turns.')}
                    </p>
                  </div>
                </div>

                <div className="pf-scenario">
                  <label className="form__group">
                    <span className="form__label">{t('New minimum hourly rate')}</span>
                    <input
                      className="form__input"
                      type="number"
                      min="0"
                      step="0.05"
                      inputMode="decimal"
                      value={minimumRate}
                      onChange={(event) => setMinimumRate(event.target.value)}
                    />
                  </label>
                  <label className="form__group">
                    <span className="form__label">{t('New unsocial-hours premium (optional)')}</span>
                    <input
                      className="form__input"
                      type="number"
                      min="0"
                      step="0.5"
                      inputMode="decimal"
                      placeholder={t('Unchanged')}
                      value={unsocialPremium}
                      onChange={(event) => setUnsocialPremium(event.target.value)}
                    />
                  </label>
                  {currentMinimum !== null && (
                    <div className="pf-scenario__quick" role="group" aria-label={t('Quick increases')}>
                      {[3, 5, 10].map((percent) => (
                        <button
                          key={percent}
                          type="button"
                          className="pf-chip-button"
                          onClick={() => setMinimumRate((currentMinimum * (1 + (percent / 100))).toFixed(2))}
                        >
                          +{percent}%
                        </button>
                      ))}
                    </div>
                  )}
                  <label className="pf-check">
                    <input
                      type="checkbox"
                      checked={keepDifferentials}
                      onChange={(event) => setKeepDifferentials(event.target.checked)}
                    />
                    <span>
                      {t('Staff paid above the minimum keep the same gap over it')}
                      <small>{t('Off: only people below the new minimum are raised to it.')}</small>
                    </span>
                  </label>
                </div>

                {analysis?.scenario && scenario ? (
                  <div className={`pf-verdict pf-tone--${analysis.scenario.turnsLossMakingCount > 0 ? 'danger' : analysis.scenario.fallsBelowTargetCount > 0 ? 'warning' : 'success'}`}>
                    <strong>{scenarioHeadline(analysis.scenario, scenario, currencyCode, { t, tn })}</strong>
                    <span>
                      {t('Labour cost over the period moves by {change}; the margin across priced contracts goes from {before} to {after}.', {
                        change: formatMoneyRounded(analysis.scenario.priced.labourCostChange, currencyCode),
                        before: formatPercent(analysis.scenario.priced.before.marginPercent),
                        after: formatPercent(analysis.scenario.priced.after.marginPercent),
                      })}
                      {analysis.scenario.alreadyLossMakingCount > 0 && (
                        <> {tn(
                          '{count} contract is loss-making already.',
                          '{count} contracts are loss-making already.',
                          analysis.scenario.alreadyLossMakingCount,
                        )}</>
                      )}
                    </span>
                  </div>
                ) : (
                  <div className="pf-verdict pf-tone--neutral">
                    <span>{t('Enter a rate to see what it does to each contract.')}</span>
                  </div>
                )}
              </section>

              <section className="pf-panel">
                <div className="pf-panel__head">
                  <div>
                    <h2>{t('Margin by client')}</h2>
                    <p>{t('Worst first. Open a client to see its sites, where the labour cost goes and the case for a new rate.')}</p>
                  </div>
                </div>

                <div className="pf-table-wrap">
                  <table className="pf-table">
                    <thead>
                      <tr>
                        <th>{t('Client')}</th>
                        <th className="pf-table__num">{t('Billable hours')}</th>
                        <th className="pf-table__num">{t('Charged / hour')}</th>
                        <th className="pf-table__num">{t('Cost / hour')}</th>
                        <th className="pf-table__num">{t('Margin today')}</th>
                        {showPrevious && analysis?.previous && (
                          <th className="pf-table__num">{previousRatesLabel(analysis.previous, t)}</th>
                        )}
                        {showScenario && scenario && (
                          <th className="pf-table__num">
                            {t('At {rate}', { rate: formatMoney(scenario.minimumHourlyRate, currencyCode) })}
                          </th>
                        )}
                        <th className="pf-table__num">{t('Charge rise needed')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.contracts.map((contract) => {
                        const key = contractKey(contract.contractId);
                        const isOpen = expanded.has(key);
                        const previous = previousByContract.get(contract.contractId) ?? null;
                        const after = scenarioByContract.get(contract.contractId);
                        const openRates = contract.contractId
                          ? (siteId: string | null) => setRateModal({
                            contract: { id: contract.contractId as string, name: contract.contractName },
                            siteId,
                          })
                          : null;

                        return (
                          <React.Fragment key={key}>
                            <tr className={`pf-row${isOpen ? ' pf-row--open' : ''}`}>
                              <td>
                                <button
                                  type="button"
                                  className="pf-row__toggle"
                                  onClick={() => toggleExpanded(key)}
                                  aria-expanded={isOpen}
                                >
                                  {isOpen ? <FiChevronDown aria-hidden="true" /> : <FiChevronRight aria-hidden="true" />}
                                  <span className="pf-row__name">{contractLabel(contract, t)}</span>
                                </button>
                                <span className={`pf-chip pf-tone--${statusTone(contract.status)}`}>
                                  {statusLabel(contract.status, t)}
                                </span>
                              </td>
                              <LineCells
                                line={contract}
                                previous={previous}
                                showPrevious={showPrevious}
                                scenario={after ?? null}
                                showScenario={showScenario}
                                targetMarginPercent={settings.targetMarginPercent}
                                currencyCode={currencyCode}
                                onSetRate={openRates ? () => openRates(null) : undefined}
                              />
                            </tr>

                            {isOpen && contract.sites.map((site) => (
                              <tr key={site.siteId} className="pf-row pf-row--site">
                                <td>
                                  <span className="pf-row__site">{site.siteName}</span>
                                  {site.status === 'loss' && (
                                    <span className="pf-chip pf-tone--danger">{statusLabel(site.status, t)}</span>
                                  )}
                                </td>
                                <LineCells
                                  line={site}
                                  previous={previous?.sites.find((entry) => entry.siteId === site.siteId) ?? null}
                                  showPrevious={showPrevious}
                                  scenario={after?.sites.find((entry) => entry.siteId === site.siteId) ?? null}
                                  showScenario={showScenario}
                                  targetMarginPercent={settings.targetMarginPercent}
                                  currencyCode={currencyCode}
                                  onSetRate={openRates ? () => openRates(site.siteId) : undefined}
                                />
                              </tr>
                            ))}

                            {isOpen && (
                              <tr className="pf-row pf-row--detail">
                                <td colSpan={columnCount}>
                                  <div className="pf-detail">
                                    <div className="pf-detail__block">
                                      <h3>{t('Where the labour cost goes')}</h3>
                                      <dl className="pf-breakdown">
                                        {breakdownRows(contract).map((row) => (
                                          <React.Fragment key={row.label}>
                                            <dt>{row.label}</dt>
                                            <dd>
                                              {formatMoney(row.amount, currencyCode)}
                                              <span>{formatPercent(contract.labourCost > 0 ? (row.amount / contract.labourCost) * 100 : null)}</span>
                                            </dd>
                                          </React.Fragment>
                                        ))}
                                        <dt className="pf-breakdown__total">{t('Labour cost')}</dt>
                                        <dd className="pf-breakdown__total">{formatMoney(contract.labourCost, currencyCode)}</dd>
                                      </dl>
                                    </div>

                                    <div className="pf-detail__block">
                                      <h3>{t('The case for this client')}</h3>
                                      {contract.status === 'no_charge_rate' ? (
                                        <p className="pf-detail__copy">
                                          {contract.contractId
                                            ? t('Add what this client is charged per hour and the margin appears here.')
                                            : t('These sites belong to no client. Give them one on the Sites page to set a charge rate.')}
                                        </p>
                                      ) : (
                                        <p className="pf-detail__copy pf-detail__copy--summary">{buildSummary(contract, after)}</p>
                                      )}
                                      <div className="pf-detail__actions">
                                        {openRates && (
                                          <button type="button" className="btn btn--secondary btn--sm" onClick={() => openRates(null)}>
                                            <FiEdit2 aria-hidden="true" /> {t('Charge rates')}
                                          </button>
                                        )}
                                        {contract.status !== 'no_charge_rate' && (
                                          <button type="button" className="btn btn--secondary btn--sm" onClick={() => { void handleCopySummary(contract); }}>
                                            <FiCopy aria-hidden="true" /> {t('Copy summary')}
                                          </button>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </section>
            </>
          )}

          <section className="pf-panel">
            <div className="pf-panel__head">
              <div>
                <h2>{t('Assumptions')}</h2>
                <p>{t('Employer on-costs are what employing somebody costs on top of gross pay: employer social insurance, holiday pay, pension, uniform. They rise with pay, so a pay increase raises them too.')}</p>
              </div>
            </div>
            <form className="pf-assumptions" onSubmit={handleSaveSettings}>
              <label className="form__group">
                <span className="form__label">{t('Employer on-costs (% of gross pay)')}</span>
                <input
                  className="form__input"
                  type="number"
                  min="0"
                  max="200"
                  step="0.1"
                  inputMode="decimal"
                  value={onCostPercent}
                  onChange={(event) => setOnCostPercent(event.target.value)}
                />
              </label>
              <label className="form__group">
                <span className="form__label">{t('Target margin (% of revenue)')}</span>
                <input
                  className="form__input"
                  type="number"
                  min="0"
                  max="99"
                  step="0.1"
                  inputMode="decimal"
                  value={targetPercent}
                  onChange={(event) => setTargetPercent(event.target.value)}
                />
              </label>
              <button type="submit" className="btn btn--primary" disabled={isBusy}>
                {isBusy ? t('Saving...') : t('Save assumptions')}
              </button>
            </form>
          </section>
        </div>
      )}

      {rateModal && workspace && (
        <ChargeRateModal
          contract={rateModal.contract}
          sites={workspace.sites.filter((site) => site.clientContractId === rateModal.contract.id)}
          chargeRates={workspace.input.context.chargeRates}
          currencyCode={currencyCode}
          today={today}
          initialSiteId={rateModal.siteId}
          onClose={() => setRateModal(null)}
          onChanged={handleRatesChanged}
        />
      )}
    </div>
  );
}
